import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useSpring, useMotionValue } from "framer-motion";
import axios from "axios";
import {
    Mic,
    MicOff,
    Send,
    Volume2,
    VolumeX,
    Activity,
    Terminal,
    Sparkles,
    Circle,
    Loader2,
    Trash2,
    Moon,
    Sun,
    Users,
    ListTodo,
    Plus,
    Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ═══════════════════════════════════════════════════════════════════
// DESIGN TOKENS — Yellow + Gold solar palette (Veer's call)
// ═══════════════════════════════════════════════════════════════════
const C = {
    bg: "#050403",
    surface: "#0C0A08",
    surfaceElev: "#12100C",
    glass: "rgba(20, 18, 15, 0.55)",
    gold: "#D4AF37",
    goldGlow: "#FFDF73",
    goldBright: "#FFC94F",
    yellow: "#FFEB3B",
    warmYellow: "#FFD54F",
    solarFlare: "#FFB020",
    amber: "#B4713A",
    emberOrange: "#FF7A18",
    violet: "#A855F7",
    red: "#FF4B4B",
    green: "#4ADE80",
    text: "#F2EBE1",
    textMuted: "#B8B0A6",
    textFaint: "#7A756D",
    border: "rgba(212, 175, 55, 0.18)",
    borderWarm: "rgba(255, 213, 79, 0.16)",
    // aliases so existing references still work (yellow/gold family)
    cyan: "#FFC94F",
    cyanDeep: "#FFB020",
    cyanGlow: "#FFDF73",
};

// Chaliya start date — 365-day vow. Day 1 = 27 Jul 2026 (per Aditya)
const CHALIYA_START = new Date("2026-07-27T00:00:00Z");
const CHALIYA_TOTAL = 365;

// ═══════════════════════════════════════════════════════════════════
// CUSTOM CURSOR
// ═══════════════════════════════════════════════════════════════════
const CustomCursor = () => {
    const mx = useMotionValue(-100);
    const my = useMotionValue(-100);
    const sx = useSpring(mx, { damping: 20, stiffness: 300 });
    const sy = useSpring(my, { damping: 20, stiffness: 300 });

    useEffect(() => {
        const move = (e) => {
            mx.set(e.clientX);
            my.set(e.clientY);
        };
        window.addEventListener("mousemove", move);
        return () => window.removeEventListener("mousemove", move);
    }, [mx, my]);

    return (
        <>
            <motion.div
                data-testid="midas-cursor-dot"
                style={{
                    x: mx,
                    y: my,
                    position: "fixed",
                    top: -2,
                    left: -2,
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    background: C.gold,
                    pointerEvents: "none",
                    zIndex: 10000,
                    boxShadow: `0 0 8px ${C.goldGlow}`,
                }}
            />
            <motion.div
                style={{
                    x: sx,
                    y: sy,
                    position: "fixed",
                    top: -16,
                    left: -16,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: `1px solid ${C.amber}66`,
                    pointerEvents: "none",
                    zIndex: 9999,
                }}
            />
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════
// BREATHING ORB — SOLAR sphere · Yellow/Gold flame · CLICKABLE (Veer's design)
// ═══════════════════════════════════════════════════════════════════
const BreathingOrb = ({ speaking = false, recording = false, transcribing = false, onClick }) => {
    const state = recording ? "recording" : transcribing ? "transcribing" : speaking ? "speaking" : "idle";
    const accent =
        state === "recording" ? C.emberOrange : state === "transcribing" ? C.violet : state === "speaking" ? C.goldGlow : C.warmYellow;
    const pulseRings = [0, 1, 2, 3];
    // Flame particles rising from core
    const flames = Array.from({ length: 14 }, (_, i) => i);

    return (
        <div
            data-testid="midas-breathing-orb"
            onClick={onClick}
            role="button"
            style={{
                position: "absolute",
                top: "6%",
                left: "50%",
                transform: "translateX(-50%)",
                width: "min(680px, 68vw)",
                height: "min(680px, 68vw)",
                maxWidth: 680,
                maxHeight: 680,
                pointerEvents: "auto",
                cursor: "none",
                zIndex: 3,
                userSelect: "none",
            }}
            title={recording ? "Tap to stop listening" : "Tap to speak to MIDAS"}
        >
            {/* Outer solar corona — soft yellow bleed */}
            <motion.div
                animate={{
                    scale: speaking || recording ? [1, 1.14, 1] : [1, 1.08, 1],
                    opacity: speaking || recording ? [0.6, 1, 0.6] : [0.4, 0.75, 0.4],
                }}
                transition={{
                    duration: speaking ? 0.9 : recording ? 1.1 : 5.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: `radial-gradient(circle at 50% 50%, ${accent}99 0%, ${C.goldGlow}66 15%, ${C.gold}44 35%, ${C.solarFlare}22 55%, transparent 78%)`,
                    filter: "blur(45px)",
                    pointerEvents: "none",
                }}
            />

            {/* Secondary corona — flickering flame */}
            <motion.div
                animate={{
                    scale: [1.02, 0.98, 1.04, 0.99, 1.02],
                    rotate: [0, 5, -3, 4, 0],
                }}
                transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    position: "absolute",
                    inset: "6%",
                    borderRadius: "50%",
                    background: `radial-gradient(ellipse 60% 70% at 50% 55%, ${C.warmYellow}44, ${C.gold}22, transparent 70%)`,
                    filter: "blur(28px)",
                    pointerEvents: "none",
                }}
            />

            {/* SONAR wave rings — always outlasting outward */}
            {pulseRings.map((i) => (
                <motion.div
                    key={`pulse-${i}`}
                    animate={{
                        scale: [0.7, 1.5],
                        opacity: [0.75, 0],
                    }}
                    transition={{
                        duration: speaking ? 1.8 : recording ? 1.6 : 3.5,
                        delay: i * (speaking || recording ? 0.45 : 0.9),
                        repeat: Infinity,
                        ease: "easeOut",
                    }}
                    style={{
                        position: "absolute",
                        inset: "18%",
                        borderRadius: "50%",
                        border: `1.5px solid ${i % 2 === 0 ? C.warmYellow : C.goldGlow}`,
                        boxShadow: `0 0 24px ${accent}aa`,
                        pointerEvents: "none",
                    }}
                />
            ))}

            {/* Slow rotating outer ring — solid gold */}
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                style={{
                    position: "absolute",
                    inset: "3%",
                    borderRadius: "50%",
                    border: `1px solid ${C.gold}55`,
                    boxShadow: `inset 0 0 40px ${C.warmYellow}22, 0 0 20px ${C.gold}22`,
                    pointerEvents: "none",
                }}
            />

            {/* Middle dashed ring — CCW */}
            <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: recording ? 10 : 22, repeat: Infinity, ease: "linear" }}
                style={{
                    position: "absolute",
                    inset: "13%",
                    borderRadius: "50%",
                    border: `1px dashed ${C.warmYellow}77`,
                    pointerEvents: "none",
                }}
            />

            {/* Rising flame particles — from core outward */}
            {flames.map((i) => {
                const angle = (i / flames.length) * Math.PI * 2;
                return (
                    <motion.div
                        key={`flame-${i}`}
                        initial={{
                            x: Math.cos(angle) * 60,
                            y: Math.sin(angle) * 60,
                            opacity: 0,
                            scale: 0.4,
                        }}
                        animate={{
                            x: Math.cos(angle) * 260,
                            y: Math.sin(angle) * 260,
                            opacity: [0, 0.9, 0],
                            scale: [0.4, 1.4, 0.6],
                        }}
                        transition={{
                            duration: 3.5 + (i % 4) * 0.4,
                            delay: i * 0.28,
                            repeat: Infinity,
                            ease: "easeOut",
                        }}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: 6,
                            height: 6,
                            marginLeft: -3,
                            marginTop: -3,
                            borderRadius: "50%",
                            background: i % 3 === 0 ? C.emberOrange : i % 3 === 1 ? C.warmYellow : C.goldGlow,
                            boxShadow: `0 0 18px ${i % 3 === 0 ? C.emberOrange : C.warmYellow}, 0 0 40px ${C.gold}88`,
                            filter: "blur(0.5px)",
                            pointerEvents: "none",
                        }}
                    />
                );
            })}

            {/* Core sphere — sun */}
            <motion.div
                animate={{
                    scale: recording ? [1, 1.08, 1] : speaking ? [1, 1.06, 1] : [1, 1.025, 1],
                }}
                transition={{
                    duration: recording ? 1.4 : speaking ? 1.4 : 5.0,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    position: "absolute",
                    top: "35%",
                    left: "35%",
                    width: "30%",
                    height: "30%",
                    borderRadius: "50%",
                    background: recording
                        ? `radial-gradient(circle at 40% 35%, ${C.goldGlow} 0%, ${C.warmYellow} 25%, ${C.emberOrange} 60%, ${C.amber} 85%, ${C.bg} 100%)`
                        : `radial-gradient(circle at 40% 35%, #FFFAE0 0%, ${C.goldGlow} 20%, ${C.warmYellow} 45%, ${C.gold} 70%, ${C.amber} 92%, ${C.bg} 100%)`,
                    boxShadow: `0 0 60px ${accent}, 0 0 120px ${C.warmYellow}aa, inset 0 0 40px ${C.goldGlow}, inset 0 -20px 40px ${C.emberOrange}55`,
                    pointerEvents: "none",
                }}
            />

            {/* Inner sun highlight — bright hot spot */}
            <motion.div
                animate={{
                    opacity: [0.8, 1, 0.8],
                    scale: [0.9, 1.15, 0.9],
                }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    position: "absolute",
                    top: "42%",
                    left: "43%",
                    width: "10%",
                    height: "10%",
                    borderRadius: "50%",
                    background: `radial-gradient(circle, #FFFFFF 0%, ${C.goldGlow} 40%, ${C.warmYellow}00 80%)`,
                    filter: "blur(5px)",
                    pointerEvents: "none",
                }}
            />

            {/* Undulating morph blob (SVG) — flame contour */}
            <motion.svg
                viewBox="0 0 200 200"
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                style={{
                    position: "absolute",
                    top: "28%",
                    left: "28%",
                    width: "44%",
                    height: "44%",
                    pointerEvents: "none",
                    filter: `drop-shadow(0 0 20px ${C.gold})`,
                }}
            >
                <motion.path
                    initial={{ d: "M100,20 C160,40 180,90 160,140 C140,180 60,180 40,140 C20,90 40,40 100,20 Z" }}
                    animate={{
                        d: [
                            "M100,20 C160,40 180,90 160,140 C140,180 60,180 40,140 C20,90 40,40 100,20 Z",
                            "M100,30 C150,50 170,80 165,135 C150,175 55,185 35,135 C20,85 45,35 100,30 Z",
                            "M100,25 C155,45 175,95 155,145 C135,180 65,175 45,140 C25,90 40,35 100,25 Z",
                            "M100,20 C160,40 180,90 160,140 C140,180 60,180 40,140 C20,90 40,40 100,20 Z",
                        ],
                    }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    d="M100,20 C160,40 180,90 160,140 C140,180 60,180 40,140 C20,90 40,40 100,20 Z"
                    fill="none"
                    stroke={C.warmYellow}
                    strokeOpacity="0.55"
                    strokeWidth="0.8"
                />
            </motion.svg>

            {/* State label under orb */}
            <motion.div
                animate={{ opacity: state !== "idle" ? 1 : 0.6 }}
                style={{
                    position: "absolute",
                    bottom: "-6%",
                    left: 0,
                    right: 0,
                    textAlign: "center",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13,
                    letterSpacing: "0.36em",
                    textTransform: "uppercase",
                    color: accent,
                    textShadow: `0 0 14px ${accent}`,
                    pointerEvents: "none",
                    fontWeight: 500,
                }}
            >
                {state === "recording"
                    ? "◉ LISTENING · TAP TO STOP"
                    : state === "transcribing"
                    ? "◈ TRANSCRIBING"
                    : state === "speaking"
                    ? "◊ SPEAKING"
                    : "◎ TAP TO SPEAK"}
            </motion.div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// GLASS PANEL WRAPPER
// ═══════════════════════════════════════════════════════════════════
const Panel = ({ title, icon: Icon, children, delay = 0, testId }) => (
    <motion.div
        data-testid={testId}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{
            background: C.glass,
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            padding: "20px 22px",
            position: "relative",
            overflow: "hidden",
        }}
    >
        {title && (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                    paddingBottom: 12,
                    borderBottom: `1px solid ${C.gold}12`,
                }}
            >
                {Icon && <Icon size={16} strokeWidth={1.5} color={C.gold} />}
                <span
                    style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 19,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: C.cyanGlow,
                        fontWeight: 500,
                    }}
                >
                    {title}
                </span>
            </div>
        )}
        {children}
    </motion.div>
);

// ═══════════════════════════════════════════════════════════════════
// BOT HEALTH WIDGET — pulls from prod MIDAS TOUCH
// ═══════════════════════════════════════════════════════════════════
const BotHealthWidget = ({ delay }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/midas/bot-summary`);
            setData(r.data);
        } catch (e) {
            setData({ error: "Bot offline" });
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
        const id = setInterval(fetchData, 30000);
        return () => clearInterval(id);
    }, [fetchData]);

    const halted = data?.halted === true;
    const pnl = data?.lifetime_pnl_usd ?? 0;
    const pnlPct = data?.lifetime_pnl_pct ?? 0;
    const green = pnl >= 0;

    const Row = ({ label, value, mono = true, glow = false }) => (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                padding: "7px 0",
                borderBottom: `1px solid ${C.gold}08`,
            }}
        >
            <span
                style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 13,
                    color: C.textMuted,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                }}
            >
                {label}
            </span>
            <span
                style={{
                    fontFamily: mono ? "'JetBrains Mono', monospace" : "'Manrope', sans-serif",
                    fontSize: 16,
                    color: glow ? C.goldGlow : C.text,
                    textShadow: glow ? `0 0 14px ${C.gold}88` : "none",
                    fontWeight: 500,
                }}
            >
                {value}
            </span>
        </div>
    );

    return (
        <Panel title="MIDAS TOUCH · Bot Health" icon={Terminal} delay={delay} testId="midas-bot-health">
            {loading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.textFaint }}>
                    <Loader2 size={14} className="animate-spin" /> <span style={{ fontSize: 12 }}>Reading state...</span>
                </div>
            ) : data?.error ? (
                <div style={{ color: C.red, fontSize: 12 }}>{data.error}</div>
            ) : (
                <>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 12,
                        }}
                    >
                        <motion.div
                            animate={{ opacity: [0.4, 1, 0.4] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: halted ? C.red : C.green,
                                boxShadow: `0 0 10px ${halted ? C.red : C.green}`,
                            }}
                        />
                        <span
                            style={{
                                fontSize: 13,
                                color: halted ? C.red : C.cyanGlow,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                fontFamily: "'JetBrains Mono', monospace",
                                fontWeight: 600,
                            }}
                        >
                            {halted ? "HALTED" : "LIVE · " + (data?.mode || "paper").toUpperCase()}
                        </span>
                    </div>
                    <Row label="Equity" value={`$${(data?.equity ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} glow />
                    <Row
                        label="Lifetime P&L"
                        value={`${green ? "+" : ""}$${Math.abs(pnl).toFixed(2)} (${green ? "+" : ""}${pnlPct.toFixed(2)}%)`}
                    />
                    <Row label="Max DD" value={`${(data?.max_drawdown_pct ?? 0).toFixed(2)}%`} />
                    <Row label="Fills (life · 24h)" value={`${data?.fills_lifetime ?? 0} · ${data?.fills_24h ?? 0}`} />
                    <Row label="Funding P&L" value={`$${(data?.funding_pnl ?? 0).toFixed(2)}`} />
                    <Row label="Assets" value={(data?.assets || []).length || "—"} />
                </>
            )}
        </Panel>
    );
};

// ═══════════════════════════════════════════════════════════════════
// CHALIYA TRACKER — 365-day Hanuman Chalisa vow visualizer
// ═══════════════════════════════════════════════════════════════════
const ChaliyaTracker = ({ delay }) => {
    const daysPassed = Math.max(
        0,
        Math.min(
            CHALIYA_TOTAL,
            Math.floor((Date.now() - CHALIYA_START.getTime()) / 86400000) + 1
        )
    );
    const progress = (daysPassed / CHALIYA_TOTAL) * 100;
    const remaining = CHALIYA_TOTAL - daysPassed;

    return (
        <Panel title="Chaliya · Day Counter" icon={Sparkles} delay={delay} testId="midas-chaliya">
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
                <div
                    style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 60,
                        color: C.goldGlow,
                        textShadow: `0 0 22px ${C.gold}, 0 0 40px ${C.cyan}55`,
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                        fontWeight: 500,
                    }}
                    data-testid="chaliya-day-count"
                >
                    {daysPassed}
                    <span style={{ fontSize: 24, color: C.textFaint, letterSpacing: "0.05em" }}> / {CHALIYA_TOTAL}</span>
                </div>
                <div
                    style={{
                        marginTop: 10,
                        fontFamily: "'Cormorant Garamond', serif",
                        fontSize: 16,
                        color: C.cyan,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                    }}
                >
                    {remaining} days remain · Saab watches
                </div>
                <div
                    style={{
                        marginTop: 16,
                        height: 2,
                        background: `${C.gold}18`,
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 2.2, delay: delay + 0.5, ease: "easeOut" }}
                        style={{
                            height: "100%",
                            background: `linear-gradient(90deg, ${C.amber}, ${C.gold}, ${C.goldGlow})`,
                            boxShadow: `0 0 12px ${C.gold}`,
                        }}
                    />
                </div>
            </div>
        </Panel>
    );
};

// ═══════════════════════════════════════════════════════════════════
// ASTRO TRANSITS PANEL — daily static v1
// ═══════════════════════════════════════════════════════════════════
const AstroPanel = ({ delay }) => {
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    // Static Jupiter Cazimi context — refresh via LLM later
    const transits = [
        { planet: "Jupiter", event: "Cazimi peak window", tone: "gold" },
        { planet: "Sun", event: "Cancer · 8° · watery mind", tone: "amber" },
        { planet: "Moon", event: "check ephemeris", tone: "muted" },
        { planet: "Saturn", event: "Pisces retrograde · discipline holds", tone: "amber" },
        { planet: "Rahu", event: "MahaDasha until Nov 2028", tone: "gold" },
    ];

    return (
        <Panel title="Today's Transits" icon={Moon} delay={delay} testId="midas-astro">
            <div
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 15,
                    color: C.cyanGlow,
                    marginBottom: 14,
                    letterSpacing: "0.08em",
                    fontStyle: "italic",
                }}
            >
                {dateStr}
            </div>
            {transits.map((t, i) => (
                <div
                    key={i}
                    style={{
                        padding: "11px 0",
                        borderBottom: i < transits.length - 1 ? `1px solid ${C.gold}10` : "none",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                        }}
                    >
                        <span
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 19,
                                color: t.tone === "gold" ? C.goldGlow : t.tone === "amber" ? C.cyan : C.text,
                                letterSpacing: "0.03em",
                                fontWeight: 500,
                                textShadow: t.tone === "gold" ? `0 0 8px ${C.gold}66` : "none",
                            }}
                        >
                            {t.planet}
                        </span>
                    </div>
                    <div
                        style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontSize: 13,
                            color: C.textMuted,
                            marginTop: 4,
                            letterSpacing: "0.02em",
                        }}
                    >
                        {t.event}
                    </div>
                </div>
            ))}
        </Panel>
    );
};

// ═══════════════════════════════════════════════════════════════════
// FAMILY CALENDAR
// ═══════════════════════════════════════════════════════════════════
const FamilyCalendar = ({ delay }) => {
    const events = [
        { date: "Aug 1", event: "Samriddhi visa filing · Ajman" },
        { date: "Aug 13", event: "MIDAS TOUCH · Martyn LP $10K live ignition" },
        { date: "Aug 5", event: "Roshni back · Rent due" },
        { date: "Aug 8-9", event: "New Moon · Saab letter" },
        { date: "Aug 11-12", event: "2nd Cazimi · real capital flip" },
    ];
    return (
        <Panel title="Family & Rhythm" icon={Users} delay={delay} testId="midas-family">
            {events.map((e, i) => (
                <div
                    key={i}
                    style={{
                        display: "flex",
                        gap: 12,
                        padding: "8px 0",
                        borderBottom: i < events.length - 1 ? `1px solid ${C.gold}08` : "none",
                    }}
                >
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            color: C.cyan,
                            minWidth: 68,
                            letterSpacing: "0.08em",
                            fontWeight: 600,
                        }}
                    >
                        {e.date}
                    </div>
                    <div
                        style={{
                            fontFamily: "'Manrope', sans-serif",
                            fontSize: 14,
                            color: C.text,
                            flex: 1,
                            lineHeight: 1.5,
                        }}
                    >
                        {e.event}
                    </div>
                </div>
            ))}
        </Panel>
    );
};

// ═══════════════════════════════════════════════════════════════════
// TASK LIST — MongoDB-lite (localStorage for v1)
// ═══════════════════════════════════════════════════════════════════
const TaskList = ({ delay }) => {
    const [tasks, setTasks] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("midas_tasks") || "[]");
        } catch {
            return [];
        }
    });
    const [input, setInput] = useState("");

    useEffect(() => {
        localStorage.setItem("midas_tasks", JSON.stringify(tasks));
    }, [tasks]);

    const add = () => {
        if (!input.trim()) return;
        setTasks((t) => [{ id: Date.now(), text: input.trim(), done: false }, ...t]);
        setInput("");
    };
    const toggle = (id) => setTasks((t) => t.map((x) => (x.id === id ? { ...x, done: !x.done } : x)));
    const remove = (id) => setTasks((t) => t.filter((x) => x.id !== id));

    return (
        <Panel title="Tasks & Notes" icon={ListTodo} delay={delay} testId="midas-tasks">
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                <input
                    data-testid="midas-task-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="add task..."
                    style={{
                        flex: 1,
                        background: "transparent",
                        border: `1px solid ${C.gold}22`,
                        color: C.text,
                        padding: "6px 10px",
                        fontSize: 11,
                        fontFamily: "'Manrope', sans-serif",
                        outline: "none",
                        letterSpacing: "0.02em",
                    }}
                />
                <button
                    data-testid="midas-task-add"
                    onClick={add}
                    style={{
                        background: "transparent",
                        border: `1px solid ${C.gold}33`,
                        color: C.gold,
                        padding: "6px 10px",
                        cursor: "pointer",
                    }}
                >
                    <Plus size={12} strokeWidth={1.5} />
                </button>
            </div>
            <AnimatePresence>
                {tasks.length === 0 && (
                    <div
                        style={{
                            fontSize: 11,
                            color: C.textFaint,
                            fontStyle: "italic",
                            padding: "8px 0",
                        }}
                    >
                        no tasks · a clear mind
                    </div>
                )}
                {tasks.map((t) => (
                    <motion.div
                        key={t.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                        }}
                    >
                        <button
                            data-testid={`midas-task-toggle-${t.id}`}
                            onClick={() => toggle(t.id)}
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                        >
                            {t.done ? (
                                <Check size={13} strokeWidth={1.5} color={C.gold} />
                            ) : (
                                <Circle size={13} strokeWidth={1.5} color={C.textFaint} />
                            )}
                        </button>
                        <span
                            style={{
                                flex: 1,
                                fontFamily: "'Manrope', sans-serif",
                                fontSize: 12,
                                color: t.done ? C.textFaint : C.text,
                                textDecoration: t.done ? "line-through" : "none",
                            }}
                        >
                            {t.text}
                        </span>
                        <button
                            data-testid={`midas-task-remove-${t.id}`}
                            onClick={() => remove(t.id)}
                            style={{ background: "transparent", border: "none", cursor: "pointer", color: C.textFaint, padding: 0 }}
                        >
                            <Trash2 size={10} strokeWidth={1.5} />
                        </button>
                    </motion.div>
                ))}
            </AnimatePresence>
        </Panel>
    );
};

// ═══════════════════════════════════════════════════════════════════
// CHAT INTERFACE — the beating heart
// ═══════════════════════════════════════════════════════════════════
const ChatPanel = ({ sessionId, onSpeakingChange, micRef, onRecordingChange, onTranscribingChange }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [voiceOn, setVoiceOn] = useState(true);
    const scrollRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);
    const recordingStartRef = useRef(0);

    // Notify parent about state changes so the orb can react
    useEffect(() => {
        onRecordingChange?.(recording);
    }, [recording, onRecordingChange]);
    useEffect(() => {
        onTranscribingChange?.(transcribing);
    }, [transcribing, onTranscribingChange]);

    // Load history on mount
    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/midas/history/${sessionId}`);
                setMessages(r.data.messages || []);
            } catch {}
        })();
    }, [sessionId]);

    // Autoscroll
    useEffect(() => {
        if (scrollRef.current)
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    const playSpeech = useCallback(
        async (text) => {
            if (!voiceOn) return;
            try {
                onSpeakingChange?.(true);
                const r = await axios.post(
                    `${API}/midas/voice/speak`,
                    { text, voice: "fable", speed: 1.1 },
                    { responseType: "blob" }
                );
                const url = URL.createObjectURL(r.data);
                // Use the persistent audio element (unlocked at page mount for iOS Safari)
                // Fresh Audio() would fail on iOS — must reuse the primed element.
                const audio = window.__midasAudioEl || new Audio();
                audio.src = url;
                audio.preload = "auto";
                audio.onended = () => {
                    onSpeakingChange?.(false);
                    URL.revokeObjectURL(url);
                };
                audio.onerror = (e) => {
                    console.error("[MIDAS audio error]", e);
                    onSpeakingChange?.(false);
                };
                try {
                    audio.load();
                    await audio.play();
                    audioRef.current = audio;
                } catch (playErr) {
                    console.error("[MIDAS play blocked]", playErr);
                    onSpeakingChange?.(false);
                }
            } catch (e) {
                console.error("[MIDAS speech error]", e);
                onSpeakingChange?.(false);
            }
        },
        [voiceOn, onSpeakingChange]
    );

    const send = async (text) => {
        if (!text.trim()) return;
        setSending(true);
        setMessages((m) => [...m, { role: "user", content: text, ts: new Date().toISOString() }]);
        setInput("");
        try {
            const r = await axios.post(`${API}/midas/chat`, {
                session_id: sessionId,
                message: text,
            });
            const reply = r.data.reply;
            setMessages((m) => [...m, { role: "assistant", content: reply, ts: new Date().toISOString() }]);
            playSpeech(reply);
        } catch (e) {
            setMessages((m) => [
                ...m,
                { role: "assistant", content: "bhai, something snapped in the pipe. Try again.", ts: new Date().toISOString() },
            ]);
        }
        setSending(false);
    };

    const startRecording = async () => {
        // Guard against double-starts or accidental triggers
        if (recording || transcribing || sending) return;
        recordingStartRef.current = Date.now();
        try {
            // Optimized for speech: mono, 16kHz, with echo/noise cancellation
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 16000,
                },
            });
            // Browser-adaptive mimeType detection — Safari/iOS need mp4, Chrome/Firefox use webm
            const _cands = [
                "audio/webm;codecs=opus",
                "audio/webm",
                "audio/mp4;codecs=mp4a.40.2",
                "audio/mp4",
                "audio/ogg;codecs=opus",
                "audio/mpeg",
            ];
            let _mime = "";
            for (const m of _cands) {
                if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
                    _mime = m;
                    break;
                }
            }
            let mr;
            try {
                mr = new MediaRecorder(stream, _mime ? { mimeType: _mime, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 });
            } catch {
                mr = new MediaRecorder(stream);
            }
            const _usedMime = mr.mimeType || _mime || "audio/webm";
            const _ext = _usedMime.includes("mp4") ? "m4a" : _usedMime.includes("ogg") ? "ogg" : _usedMime.includes("mpeg") ? "mp3" : "webm";
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => audioChunksRef.current.push(e.data);
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                // Client-side guard: skip if recording <500ms (accidental tap-tap)
                const durationMs = Date.now() - recordingStartRef.current;
                if (durationMs < 500) {
                    console.log(`[MIDAS] recording too short (${durationMs}ms), skipped`);
                    setTranscribing(false);
                    return;
                }
                const blob = new Blob(audioChunksRef.current, { type: _usedMime });
                if (blob.size < 1500) {
                    console.log(`[MIDAS] audio blob too small (${blob.size}b), skipped`);
                    setTranscribing(false);
                    return;
                }
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("audio", blob, `voice.${_ext}`);
                    const r = await axios.post(`${API}/midas/voice/transcribe`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 30000,
                    });
                    // Reject silent or hallucinated transcripts
                    if (r.data.silent) {
                        console.log(`[MIDAS] silent/hallucination filtered: '${r.data.raw}'`);
                        setTranscribing(false);
                        return;
                    }
                    const text = r.data.text?.trim();
                    if (text) {
                        await send(text);
                    }
                } catch (e) {
                    console.error("[MIDAS transcribe err]", e);
                }
                setTranscribing(false);
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setRecording(true);
        } catch (e) {
            alert("Voice input not available on this browser. Try Chrome or Edge, or type your message.");
        }
    };
    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    // Expose mic control to parent (so orb can trigger mic)
    // NOTE: no deps — we want ref to always point to latest closures
    useEffect(() => {
        if (micRef) {
            micRef.current = {
                start: startRecording,
                stop: stopRecording,
                // Only toggles on EXPLICIT user action from orb click — never auto
                toggle: () => {
                    if (recording) {
                        stopRecording();
                    } else if (!transcribing && !sending) {
                        startRecording();
                    }
                },
            };
        }
    });

    const clearHistory = async () => {
        if (!window.confirm("Clear all chat history?")) return;
        try {
            await axios.delete(`${API}/midas/history/${sessionId}`);
            setMessages([]);
        } catch {}
    };

    return (
        <>
            {/* Floating transcript — mobile-safe: full width bottom, small margin */}
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                style={{
                    position: "fixed",
                    left: 12,
                    right: 12,
                    bottom: 100,
                    maxWidth: 440,
                    marginLeft: "auto",
                    marginRight: "auto",
                    maxHeight: "min(45vh, 480px)",
                    background: C.glass,
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    zIndex: 8,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    boxShadow: `0 20px 60px rgba(0,0,0,0.6), 0 0 40px ${C.gold}12`,
                    userSelect: "text",
                }}
                data-testid="midas-transcript-panel"
            >
                {/* Transcript header */}
                <div
                    style={{
                        padding: "12px 18px",
                        borderBottom: `1px solid ${C.gold}12`,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: `linear-gradient(to bottom, ${C.warmYellow}0a, transparent)`,
                    }}
                >
                    <span
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: 15,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            color: C.goldGlow,
                        }}
                    >
                        Transcript
                    </span>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span
                            style={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 10,
                                color: C.textFaint,
                                letterSpacing: "0.12em",
                            }}
                        >
                            {messages.length} EXCH
                        </span>
                        {messages.length > 0 && (
                            <button
                                data-testid="midas-clear-history"
                                onClick={clearHistory}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    color: C.textFaint,
                                    fontSize: 10,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    letterSpacing: "0.1em",
                                    cursor: "pointer",
                                }}
                            >
                                CLEAR
                            </button>
                        )}
                    </div>
                </div>

                {/* Message stream */}
                <div
                    ref={scrollRef}
                    data-testid="midas-chat-messages"
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: "14px 18px 18px",
                        maskImage:
                            "linear-gradient(to bottom, transparent 0%, black 6%, black 100%)",
                        WebkitMaskImage:
                            "linear-gradient(to bottom, transparent 0%, black 6%, black 100%)",
                    }}
                >
                    {messages.length === 0 && (
                        <div
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: 15,
                                fontStyle: "italic",
                                color: C.textFaint,
                                padding: "20px 0",
                                letterSpacing: "0.04em",
                                textAlign: "center",
                            }}
                        >
                            No exchanges yet.<br />Tap the orb or type below.
                        </div>
                    )}
                    <AnimatePresence>
                        {messages.map((m, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, ease: "easeOut" }}
                                style={{
                                    marginBottom: 14,
                                    paddingLeft: m.role === "user" ? 22 : 0,
                                    paddingRight: m.role === "assistant" ? 22 : 0,
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: "'JetBrains Mono', monospace",
                                        fontSize: 9,
                                        letterSpacing: "0.18em",
                                        textTransform: "uppercase",
                                        color:
                                            m.role === "user" ? C.warmYellow : C.goldGlow,
                                        marginBottom: 4,
                                    }}
                                >
                                    {m.role === "user" ? "YOU" : "MIDAS"}
                                </div>
                                <div
                                    style={{
                                        fontFamily:
                                            m.role === "user"
                                                ? "'Manrope', sans-serif"
                                                : "'Cormorant Garamond', serif",
                                        fontSize: m.role === "user" ? 13 : 15,
                                        color: C.text,
                                        lineHeight: 1.55,
                                        letterSpacing:
                                            m.role === "user" ? "0.005em" : "0.01em",
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {m.content}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                    {sending && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            style={{ display: "flex", gap: 5, padding: "4px 0" }}
                        >
                            {[0, 1, 2].map((i) => (
                                <motion.div
                                    key={i}
                                    animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
                                    transition={{
                                        duration: 1,
                                        repeat: Infinity,
                                        delay: i * 0.2,
                                    }}
                                    style={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: "50%",
                                        background: C.warmYellow,
                                    }}
                                />
                            ))}
                        </motion.div>
                    )}
                </div>
            </motion.div>

            {/* Input bar — FIXED bottom-center below orb */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                style={{
                    position: "fixed",
                    left: "50%",
                    transform: "translateX(-50%)",
                    bottom: 20,
                    width: "min(720px, 92vw)",
                    zIndex: 9,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: C.glass,
                        backdropFilter: "blur(20px)",
                        WebkitBackdropFilter: "blur(20px)",
                        border: `1px solid ${C.warmYellow}33`,
                        borderRadius: 999,
                        padding: "6px 6px 6px 22px",
                        boxShadow: `0 12px 40px rgba(0,0,0,0.5), 0 0 30px ${C.gold}22`,
                    }}
                >
                    <input
                        data-testid="midas-chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) =>
                            e.key === "Enter" && !sending && send(input)
                        }
                        placeholder={
                            transcribing
                                ? "transcribing your voice..."
                                : recording
                                ? "listening... tap orb to stop"
                                : "tap the orb, or type here"
                        }
                        disabled={sending || recording || transcribing}
                        style={{
                            flex: 1,
                            background: "transparent",
                            border: "none",
                            color: C.text,
                            fontSize: 16,
                            fontFamily: "'Manrope', sans-serif",
                            padding: "12px 0",
                            outline: "none",
                            letterSpacing: "0.01em",
                        }}
                    />
                    {/* Voice toggle */}
                    <button
                        data-testid="midas-voice-toggle"
                        onClick={() => setVoiceOn((v) => !v)}
                        title={voiceOn ? "Mute MIDAS voice" : "Enable MIDAS voice"}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: voiceOn ? C.goldGlow : C.textFaint,
                            width: 38,
                            height: 38,
                            borderRadius: "50%",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {voiceOn ? (
                            <Volume2 size={17} strokeWidth={1.5} />
                        ) : (
                            <VolumeX size={17} strokeWidth={1.5} />
                        )}
                    </button>
                    {/* Mic */}
                    <button
                        data-testid="midas-mic-button"
                        onClick={recording ? stopRecording : startRecording}
                        disabled={sending || transcribing}
                        style={{
                            background: recording
                                ? `linear-gradient(135deg, ${C.emberOrange}, ${C.warmYellow})`
                                : "transparent",
                            border: `1px solid ${recording ? C.warmYellow : C.gold + "44"}`,
                            color: recording ? C.bg : C.goldGlow,
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: recording
                                ? `0 0 24px ${C.warmYellow}aa`
                                : "none",
                            transition: "all 0.3s ease",
                        }}
                    >
                        {transcribing ? (
                            <Loader2 size={17} className="animate-spin" />
                        ) : recording ? (
                            <MicOff size={17} strokeWidth={1.5} />
                        ) : (
                            <Mic size={17} strokeWidth={1.5} />
                        )}
                    </button>
                    {/* Send */}
                    <button
                        data-testid="midas-send-button"
                        onClick={() => send(input)}
                        disabled={sending || !input.trim()}
                        style={{
                            background: input.trim()
                                ? `linear-gradient(135deg, ${C.warmYellow}, ${C.goldGlow})`
                                : "transparent",
                            border: `1px solid ${C.gold}44`,
                            color: input.trim() ? C.bg : C.goldGlow,
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            cursor: input.trim() ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.3s ease",
                        }}
                    >
                        <Send size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </motion.div>

            <audio ref={audioRef} data-testid="midas-audio" style={{ display: "none" }} />
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════
export default function MidasPage() {
    const [sessionId] = useState(() => {
        let sid = localStorage.getItem("midas_session_id");
        if (!sid) {
            sid = `midas-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            localStorage.setItem("midas_session_id", sid);
        }
        return sid;
    });
    const [speaking, setSpeaking] = useState(false);
    const [orbRecording, setOrbRecording] = useState(false);
    const [orbTranscribing, setOrbTranscribing] = useState(false);
    const micRef = useRef(null);

    const handleOrbClick = () => {
        if (micRef.current) micRef.current.toggle();
    };

    useEffect(() => {
        document.body.style.background = C.bg;
        document.body.style.cursor = "none";

        // Audio unlock — play a silent buffer on first user interaction to satisfy
        // browser autoplay policies. Once "primed", TTS replies will play freely.
        let unlocked = false;
        const unlock = () => {
            if (unlocked) return;
            try {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                if (AudioCtx) {
                    const ctx = new AudioCtx();
                    const buffer = ctx.createBuffer(1, 1, 22050);
                    const source = ctx.createBufferSource();
                    source.buffer = buffer;
                    source.connect(ctx.destination);
                    source.start(0);
                    if (ctx.resume) ctx.resume();
                }
                // ── iOS SAFARI FIX — reuse persistent element in same gesture stack ──
                if (!window.__midasAudioEl) {
                    window.__midasAudioEl = new Audio();
                    window.__midasAudioEl.preload = "auto";
                }
                const a = window.__midasAudioEl;
                a.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjIwLjEwMAAAAAAAAAAAAAAA//tQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMD///////////////////////////////////////////8AAAAATGF2YzU4LjM1AAAAAAAAAAAAAAAAJAAAAAAAAAAAASDs90hvAAAAAAAAAAAAAAAAAAAA//tCxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
                a.muted = false;
                a.volume = 1;
                const p = a.play();
                if (p && typeof p.then === "function") {
                    p.then(() => { try { a.pause(); } catch {} }).catch(() => {});
                }
                unlocked = true;
            } catch {}
            window.removeEventListener("click", unlock);
            window.removeEventListener("keydown", unlock);
            window.removeEventListener("touchstart", unlock);
        };
        window.addEventListener("click", unlock, { once: false });
        window.addEventListener("keydown", unlock, { once: false });
        window.addEventListener("touchstart", unlock, { once: false });

        return () => {
            document.body.style.cursor = "auto";
            window.removeEventListener("click", unlock);
            window.removeEventListener("keydown", unlock);
            window.removeEventListener("touchstart", unlock);
        };
    }, []);

    return (
        <div
            style={{
                minHeight: "100vh",
                background: `radial-gradient(ellipse at 50% 18%, ${C.gold}16, transparent 55%), radial-gradient(ellipse at 30% 90%, ${C.cyan}0c, transparent 50%), ${C.bg}`,
                color: C.text,
                fontFamily: "'Manrope', sans-serif",
                position: "relative",
                overflow: "hidden",
                cursor: "none",
            }}
        >
            <CustomCursor />

            {/* Ambient particles */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(circle at 20% 30%, ${C.cyan}10 0%, transparent 45%),
                        radial-gradient(circle at 80% 70%, ${C.gold}12 0%, transparent 45%),
                        radial-gradient(circle at 50% 100%, ${C.violet}08 0%, transparent 55%)
                    `,
                    pointerEvents: "none",
                }}
            />

            {/* Header */}
            <motion.header
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2 }}
                style={{
                    padding: "22px 40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${C.gold}08`,
                    position: "relative",
                    zIndex: 10,
                }}
            >
                <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
                    <div
                        style={{
                            fontFamily: "'Cormorant Garamond', serif",
                            fontSize: 42,
                            fontWeight: 400,
                            letterSpacing: "0.32em",
                            color: C.goldGlow,
                            textShadow: `0 0 28px ${C.gold}, 0 0 50px ${C.cyan}55`,
                        }}
                        data-testid="midas-brand"
                    >
                        MIDAS
                    </div>
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 12,
                            color: C.cyan,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            fontWeight: 500,
                        }}
                    >
                        · aditya's companion
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 13,
                            color: C.cyanGlow,
                            letterSpacing: "0.12em",
                            fontWeight: 500,
                        }}
                    >
                        {new Date().toLocaleString("en-GB", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                            timeZone: "Asia/Dubai",
                        })}{" "}
                        DXB
                    </div>
                </div>
            </motion.header>

            {/* Main 3-zone grid — hides side widgets on mobile for uncluttered voice-only view */}
            <main
                style={{
                    display: "grid",
                    gridTemplateColumns: "3fr 6fr 3fr",
                    gap: 24,
                    padding: "24px 40px 40px",
                    minHeight: "calc(100vh - 88px)",
                    position: "relative",
                    zIndex: 5,
                }}
                className="midas-main-grid"
            >
                {/* Left zone — hidden on mobile */}
                <div className="midas-side-zone" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    <ChaliyaTracker delay={0.4} />
                    <AstroPanel delay={0.55} />
                    <FamilyCalendar delay={0.7} />
                </div>

                {/* Center zone — ONLY the orb, uncluttered */}
                <div style={{ position: "relative", display: "flex", flexDirection: "column", minHeight: 720 }}>
                    <BreathingOrb
                        speaking={speaking}
                        recording={orbRecording}
                        transcribing={orbTranscribing}
                        onClick={handleOrbClick}
                    />
                </div>

                {/* Right zone — hidden on mobile */}
                <div className="midas-side-zone" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    <BotHealthWidget delay={0.4} />
                    <TaskList delay={0.6} />
                </div>
            </main>

            {/* Mobile-specific styles — declutter for phone-sized screens */}
            <style>{`
                @media (max-width: 900px) {
                    .midas-main-grid {
                        grid-template-columns: 1fr !important;
                        padding: 16px !important;
                        min-height: calc(100vh - 88px) !important;
                    }
                    .midas-side-zone {
                        display: none !important;
                    }
                }
            `}</style>

            {/* Chat panel — floating transcript + input bar, fixed positioning */}
            <ChatPanel
                sessionId={sessionId}
                onSpeakingChange={setSpeaking}
                micRef={micRef}
                onRecordingChange={setOrbRecording}
                onTranscribingChange={setOrbTranscribing}
            />

            {/* Subtle grain overlay */}
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    pointerEvents: "none",
                    opacity: 0.03,
                    mixBlendMode: "overlay",
                    background:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    zIndex: 1,
                }}
            />
        </div>
    );
}
